/**
 * A trader's execution capabilities, and the bounds on them — ROADMAP 9.12.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ONE DEFINITION OF EVERY CAP, BECAUSE FOUR PLACES NOW SET THESE
 *
 * `UserExecutionPolicy` is a CAPABILITY TABLE: rows read on the order path
 * that decide what the dispatcher is allowed to do for one trader. Until now
 * it was written by hand, so the only bound that existed was the engine's own
 * clamp at the point of use.
 *
 * It is now written from four places — the admin console, its HTTP boundary,
 * the `policy` command, and the engine's clamp — and a cap that lives in four
 * files is a cap that will disagree with itself. This module is the single
 * definition, and `orderSlicer.limitBufferFor` clamps against the same
 * constant rather than its own copy.
 *
 * THE CLAMP SURVIVES ANYWAY, AND MUST. Validating here does not make the
 * engine's clamp redundant: a row can predate these bounds, be written by a
 * future migration, or be edited straight in psql. §"never trust the
 * database" applies to our own tables, so the order path still clamps what it
 * reads.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT EACH CAPABILITY IS
 *
 * ORDER SLICING — one leg over the exchange FREEZE QUANTITY (a per-ORDER
 * limit, not a position limit) is sent as several orders instead of being
 * rejected whole. `sliceMaxQty` is the blast radius: slicing without a
 * ceiling turns a fat-fingered extra zero into two hundred live orders, and
 * the freeze limit stops none of them because it is per order.
 *
 * AUTO-CHASE — a re-pricing sweeper. A resting order's limit price is MODIFIED
 * toward the touch on a fixed interval until it fills. `autoChaseMaxMs`
 * bounds it, and the bound is not optional: an unbounded chase is an order
 * that follows a running market until the session ends.
 *
 * MANUAL NETTING — a chain click on a strike this login already holds in the
 * MANUAL block REDUCES that leg instead of opening a second one. Strategy and
 * combined-premium baskets are never netted.
 *
 * LIMIT BUFFER — how far THROUGH the touch a market-like order is priced, as
 * a fraction. A SLIPPAGE CAP and not a target: a limit order fills at the
 * book's price, so a buy capped 5% above the offer still fills AT the offer
 * when the offer is there.
 * ─────────────────────────────────────────────────────────────────────────────
 */
/**
 * The ceiling on `limitBufferPct`.
 *
 * 20% through the touch is already far past anything deliberate; the
 * exchange's own price band is the real limit and the catalogue does not
 * carry it (RMS gate 7 is permanently NOT_EVALUATED), so this exists to stop
 * a typo pricing an order into nonsense rather than to express a view.
 */
export const MAX_LIMIT_BUFFER_PCT = 0.2;
/**
 * How many orders one leg may become when nothing is set for the trader.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE BOUND IS TIME IN THE MARKET, NOT ANYTHING ON OUR SERVERS
 *
 * Slices are paced by the ORDER RATE BUDGET — `burst: 4, perSec: 5`, chosen
 * so `burst + perSec` stays under SEBI's threshold of 10 per second. The
 * database rows and the HTTP calls are trivial; what a slice count buys is
 * SECONDS, and the arithmetic is fixed:
 *
 *     seconds ≈ (orders − burst) / perSec   →   100 orders ≈ 19s for one leg
 *
 * A basket is interleaved, so two legs of 100 is 200 orders and about 39
 * seconds before the structure is fully in the market. Every second of that
 * is a second the position is half on, and for a spread half on is
 * directional.
 *
 * Raised from 40 to 100 on 2026-11-01 at the owner's request, which takes the
 * worst case for one leg from ~7s to ~19s. The number is a judgement about
 * acceptable exposure, not a technical limit, which is exactly why it is now
 * per trader.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export const DEFAULT_MAX_SLICE_ORDERS = 100;
export const EXECUTION_POLICY_LIMITS = {
    /**
     * UNITS, and the upper bound is `BrokerOrder.qty`'s — a Postgres `int4`
     * stops at 2,147,483,647, and a value past it does not refuse, it
     * OVERFLOWS. Matches `INTENT_LIMITS.maxQty`, which bounds the same column
     * from the order path.
     */
    sliceMaxQty: { min: 0, max: 2_000_000_000 },
    /**
     * Orders one leg may become. 0 means `DEFAULT_MAX_SLICE_ORDERS`.
     *
     * The ceiling is 200 because that is ~39 seconds of release for ONE leg at
     * the rate budget, and a leg that takes most of a minute to reach the
     * market is a different instrument from the one the trader priced.
     */
    sliceMaxOrders: { min: 0, max: 200 },
    /** One hour. Longer than any session, so it is a sanity bound, not a policy. */
    autoChaseMaxMs: { min: 0, max: 3_600_000 },
    limitBufferPct: { min: 0, max: MAX_LIMIT_BUFFER_PCT },
};
/** The keys that are booleans, so a bulk action can name one generically. */
export const EXECUTION_CAPABILITIES = ['sliceEnabled', 'autoChaseEnabled', 'manualNetting'];
export const CAPABILITY_LABEL = {
    sliceEnabled: 'Order slicing',
    autoChaseEnabled: 'Auto-price',
    manualNetting: 'Manual netting',
};
/**
 * Validate a patch, returning the sentence to show or null when it is sound.
 *
 * Shared by the HTTP boundary and the console, so the screen can refuse
 * before a round trip and say the SAME words the server would. A UI that
 * promises what the server refuses is the mismatch this codebase keeps
 * removing.
 */
export function validateExecutionPolicyPatch(patch) {
    const bools = [
        'manualNetting',
        'sliceEnabled',
        'autoChaseEnabled',
    ];
    for (const key of bools) {
        const v = patch[key];
        if (v !== undefined && typeof v !== 'boolean')
            return `${key} must be true or false.`;
    }
    const nums = [
        ['sliceMaxQty', 'The slice ceiling'],
        ['sliceMaxOrders', 'The orders-per-leg ceiling'],
        ['autoChaseMaxMs', 'The chase limit'],
        ['limitBufferPct', 'The limit buffer'],
    ];
    for (const [key, label] of nums) {
        const v = patch[key];
        if (v === undefined)
            continue;
        const { min, max } = EXECUTION_POLICY_LIMITS[key];
        if (typeof v !== 'number' || !Number.isFinite(v))
            return `${label} must be a number.`;
        if (v < min || v > max) {
            return `${label} must be between ${min.toLocaleString('en-IN')} and ${max.toLocaleString('en-IN')}.`;
        }
        // Two of the three are integers; a fractional unit count or millisecond
        // is a typo rather than a preference.
        if (key !== 'limitBufferPct' && !Number.isInteger(v))
            return `${label} must be a whole number.`;
    }
    if (Object.keys(patch).length === 0)
        return 'Nothing to change.';
    return null;
}
/**
 * The capabilities this trader actually has, counting an absent row as off.
 *
 * Exported so the console and any later reader agree about what a null policy
 * means, rather than each spelling out the same `?? false`.
 */
export function capabilityOf(policy, capability) {
    return policy === null ? false : policy[capability];
}
//# sourceMappingURL=executionPolicy.js.map